/**
 * DECKSTR.GG — Meta Analysis Panel
 * ─────────────────────────────────
 * Full meta feature: Overview, Tier List, Card Data, Threat Analysis.
 * Matches Untapped.gg feature set + our unique probability engine.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Search, Plus, X, Shield, Swords, Target, TrendingUp, TrendingDown, Minus,
    ChevronDown, AlertTriangle, Trash2, BarChart3, Crosshair, Info, Crown,
    ArrowUpRight, ArrowDownRight, Trophy, Layers, CreditCard, Activity,
    Eye, ExternalLink, FlaskConical, Clock, RefreshCw
} from 'lucide-react';
import {
    type Archetype, type MetaCard, type ManaColor, type Tier, type Trend,
    COLOR_PIPS, TIER_META, getMetaForFormat
} from '../lib/metaData';
import { metaService, supabase } from '../lib/supabase';

// ─── Sub-tab type ────────────────────────────────────────────────
type MetaView = 'overview' | 'tierlist' | 'cards' | 'threats' | 'report';

// ─── Hypergeometric math ─────────────────────────────────────────
function logFact(n: number) { let r = 0; for (let i = 2; i <= n; i++) r += Math.log(i); return r; }
function logBin(n: number, k: number) { return k < 0 || k > n ? -Infinity : logFact(n) - logFact(k) - logFact(n - k); }
function hgPMF(N: number, K: number, n: number, k: number) {
    return k < 0 || k > Math.min(K, n) || n - k > N - K ? 0 : Math.exp(logBin(K, k) + logBin(N - K, n - k) - logBin(N, n));
}
function pAtLeast1(deck: number, copies: number, seen: number) { return 1 - hgPMF(deck, copies, seen, 0); }
function cardsSeen(t: number, play: boolean) { return t <= 0 ? 7 : play ? 7 + t - 1 : 7 + t; }

// ─── Format presets ──────────────────────────────────────────────
interface Fmt { key: string; label: string; deck: number; maxT: number; icon: string; color: string; bg: string; border: string; }
const FMTS: Fmt[] = [
    { key: 'standard', label: 'Standard', deck: 60, maxT: 6, icon: '⚡', color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30' },
    { key: 'pioneer', label: 'Pioneer', deck: 60, maxT: 6, icon: '🔥', color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30' },
    { key: 'modern', label: 'Modern', deck: 60, maxT: 6, icon: '⚙️', color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30' },
    { key: 'legacy', label: 'Legacy', deck: 60, maxT: 6, icon: '👑', color: 'text-purple-400', bg: 'bg-purple-500/15', border: 'border-purple-500/30' },
    { key: 'commander', label: 'Commander', deck: 99, maxT: 8, icon: '🏰', color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30' },
    { key: 'pauper', label: 'Pauper', deck: 60, maxT: 6, icon: '🪙', color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30' },
    { key: 'limited', label: 'Limited', deck: 40, maxT: 6, icon: '📦', color: 'text-teal-400', bg: 'bg-teal-500/15', border: 'border-teal-500/30' },
];

// ─── Visual helpers ──────────────────────────────────────────────
type ThreatCat = 'removal' | 'counter' | 'sweeper' | 'engine' | 'threat' | 'other';
const CAT_META: Record<ThreatCat, { label: string; icon: string; color: string; bg: string }> = {
    removal: { label: 'Removal', icon: '🎯', color: 'text-red-400', bg: 'bg-red-500/10' },
    counter: { label: 'Counter', icon: '🛡️', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    sweeper: { label: 'Sweeper', icon: '🌊', color: 'text-purple-400', bg: 'bg-purple-500/10' },
    engine: { label: 'Engine', icon: '⚙️', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    threat: { label: 'Threat', icon: '⚔️', color: 'text-orange-400', bg: 'bg-orange-500/10' },
    other: { label: 'Other', icon: '📋', color: 'text-gray-400', bg: 'bg-gray-500/10' },
};
function riskCol(p: number) { return p >= 70 ? 'from-red-500 to-red-600' : p >= 50 ? 'from-orange-500 to-orange-600' : p >= 30 ? 'from-yellow-500 to-yellow-600' : p >= 15 ? 'from-green-500 to-green-600' : 'from-gray-500 to-gray-600'; }
function riskTxt(p: number) { return p >= 70 ? 'text-red-400' : p >= 50 ? 'text-orange-400' : p >= 30 ? 'text-yellow-400' : p >= 15 ? 'text-green-400' : 'text-gray-400'; }
function riskLbl(p: number) { return p >= 80 ? 'Very High' : p >= 60 ? 'High' : p >= 40 ? 'Moderate' : p >= 20 ? 'Low' : 'Minimal'; }

const TrendIcon = ({ trend }: { trend: Trend }) => trend === 'rising' ? <ArrowUpRight className="size-3 text-green-400" /> : trend === 'falling' ? <ArrowDownRight className="size-3 text-red-400" /> : <Minus className="size-3 text-white/30" />;
const ColorPips = ({ colors }: { colors: ManaColor[] }) => (
    <div className="flex gap-0.5">{colors.map((c, i) => <span key={i} className={`size-4 rounded-full ${COLOR_PIPS[c].bg} ${COLOR_PIPS[c].text} text-[8px] font-bold flex items-center justify-center`}>{COLOR_PIPS[c].label}</span>)}</div>
);
const WRBadge = ({ wr }: { wr: number }) => <span className={`font-bold tabular-nums ${wr >= 55 ? 'text-green-400' : wr >= 52 ? 'text-amber-400' : wr >= 50 ? 'text-white/70' : 'text-red-400'}`}>{wr.toFixed(1)}%</span>;

// ─── Scryfall search ─────────────────────────────────────────────
async function searchScryfall(q: string) {
    if (!q || q.length < 2) return [];
    try { const r = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&order=name&unique=cards`); if (!r.ok) return []; const d = await r.json(); return (d.data || []).slice(0, 8); } catch { return []; }
}
function classifyCard(c: any): ThreatCat {
    const t = (c.oracle_text || '').toLowerCase();
    const tp = (c.type_line || '').toLowerCase();

    // Score each category — highest wins
    const scores: Record<ThreatCat, number> = { removal: 0, counter: 0, sweeper: 0, engine: 0, threat: 0, other: 0 };

    // ── Sweeper signals (board-wide effects) ──
    if (/destroy all|destroy each|exile all|exile each|damage to each|all creatures get -|each creature gets -/.test(t)) scores.sweeper += 10;
    if (/wrath|damnation|overloaded|board wipe/.test(t)) scores.sweeper += 5;

    // ── Counter signals ──
    if (/counter target spell|counter target activated|counter target triggered/.test(t)) scores.counter += 10;
    if (/counter it|counter that spell|can't be cast/.test(t)) scores.counter += 6;

    // ── Targeted removal signals ──
    if (/destroy target|exile target|target creature gets -|deals? \d+ damage to (target|any target|it)/.test(t)) scores.removal += 8;
    if (/destroy another|exile another|fights? target|target (creature|permanent|planeswalker|artifact|enchantment).*destroy/.test(t)) scores.removal += 6;
    if (tp.includes('enchantment') && /enchant (creature|permanent)/.test(t) && /doesn't untap|can't attack|can't block/.test(t)) scores.removal += 7; // Pacifism-style
    // Penalize broad words when context is NOT removal (e.g. "exile it" in Harmonize reminder text)
    if (/search your library|put .* onto the battlefield|return .* from your graveyard/.test(t)) scores.removal -= 5;

    // ── Engine signals (value, card advantage, repeatable effects) ──
    if (/whenever|at the beginning of|draw a card|create .* token|search your library|look at the top/.test(t)) scores.engine += 6;
    if (/put .* onto the battlefield|add \{|mana of any|untap target/.test(t)) scores.engine += 4;
    if (/draw|scry|mill|surveil/.test(t) && /whenever|each|your/.test(t)) scores.engine += 3;

    // ── Threat signals (proactive pressure) ──
    if (tp.includes('creature') || tp.includes('planeswalker')) scores.threat += 4;
    if (/gets? \+|power and toughness|trample|flying|haste|double strike|lifelink|menace|vigilance/.test(t)) scores.threat += 3;
    if (/attacks|deals combat damage|when .* deals damage/.test(t)) scores.threat += 3;

    // ── Type-line boosts ──
    if (tp.includes('instant') || tp.includes('sorcery')) { scores.removal += 1; scores.counter += 1; }
    if (tp.includes('creature')) scores.threat += 2;
    if (tp.includes('planeswalker')) { scores.threat += 3; scores.engine += 2; }
    if (tp.includes('enchantment') || tp.includes('artifact')) scores.engine += 1;

    // Pick the highest-scoring category
    let best: ThreatCat = 'other';
    let bestScore = 0;
    for (const [cat, score] of Object.entries(scores) as [ThreatCat, number][]) {
        if (cat === 'other') continue;
        if (score > bestScore) { bestScore = score; best = cat; }
    }
    return bestScore >= 3 ? best : 'other';
}
function getImg(c: any) { return c.image_uris?.small || c.card_faces?.[0]?.image_uris?.small || ''; }

// ─── Watchlist persistence ───────────────────────────────────────
interface WLCard { id: string; name: string; copies: number; manaCost: string; typeLine: string; imageUrl: string; category: ThreatCat; metaShare?: number; }
const SK = 'deckstr_meta_watchlist';
function loadWL(): WLCard[] { try { return JSON.parse(localStorage.getItem(SK) || '[]'); } catch { return []; } }
function saveWL(cards: WLCard[]) { localStorage.setItem(SK, JSON.stringify(cards)); }

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

interface MetaPanelProps {
    onExamineInBrew?: (archetype: Archetype) => void;
}

export function MetaPanel({ onExamineInBrew }: MetaPanelProps = {}) {
    // Helper: examine archetype in Brew tab
    const examineInBrew = (arch: Archetype) => {
        if (onExamineInBrew) {
            onExamineInBrew(arch);
        } else {
            window.location.hash = '#brew';
        }
    };
    const [fmt, setFmt] = useState<Fmt>(FMTS[0]);
    const [view, setView] = useState<MetaView>('overview');
    const [tierFilter, setTierFilter] = useState<Tier | 'all'>('all');
    const [cardSort, setCardSort] = useState<'inHandWR' | 'playedWR' | 'includedWR' | 'totalGames'>('inHandWR');

    // Threat analysis state
    const [wl, setWl] = useState<WLCard[]>(() => loadWL());
    const [sq, setSq] = useState('');
    const [sr, setSr] = useState<any[]>([]);
    const [srch, setSrch] = useState(false);
    const [showSrch, setShowSrch] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [catF, setCatF] = useState<ThreatCat | 'all'>('all');
    const [intTarget, setIntTarget] = useState(50);
    const [intTurn, setIntTurn] = useState(3);
    const stRef = useRef<any>(null);
    const siRef = useRef<HTMLInputElement>(null);

    // DB-backed archetype & card data (with seed fallback)
    const [archetypes, setArchetypes] = useState<Archetype[]>([]);
    const [cards, setCards] = useState<MetaCard[]>([]);
    const [dbLoading, setDbLoading] = useState(false);

    // Match report state
    const [reportArch, setReportArch] = useState('');
    const [reportOpp, setReportOpp] = useState('');
    const [reportResult, setReportResult] = useState<'win' | 'loss' | 'draw'>('win');
    const [reportBo, setReportBo] = useState<'bo1' | 'bo3'>('bo1');
    const [reportSending, setReportSending] = useState(false);
    const [reportSent, setReportSent] = useState(false);

    // Expanded archetype detail (key cards overlay)
    const [expandedArch, setExpandedArch] = useState<Archetype | null>(null);

    // Data freshness tracking
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [dataSource, setDataSource] = useState<'db' | 'seed'>('seed');
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => { saveWL(wl); }, [wl]);

    // Load meta data from DB, fallback to seeds
    useEffect(() => {
        let cancelled = false;
        async function loadMeta() {
            setDbLoading(true);
            try {
                const [dbArch, dbCards] = await Promise.all([
                    metaService.getArchetypes(fmt.key),
                    metaService.getCardStats(fmt.key),
                ]);
                if (cancelled) return;
                if (dbArch.length > 0) {
                    setArchetypes(dbArch.map(a => ({
                        name: a.name, colors: a.colors as ManaColor[], tier: a.tier as Tier,
                        fieldShare: Number(a.field_share), winRate: Number(a.win_rate),
                        trend: a.trend as Trend, keyCards: a.key_cards || [], matches: a.matches,
                    })));
                    setDataSource('db');
                } else {
                    setArchetypes(getMetaForFormat(fmt.key).archetypes);
                }
                if (dbCards.length > 0) {
                    setCards(dbCards.map(c => ({
                        name: c.card_name, colors: c.colors as ManaColor[],
                        inHandWR: Number(c.in_hand_wr), playedWR: Number(c.played_wr),
                        includedWR: Number(c.included_wr), totalGames: c.total_games,
                    })));
                } else {
                    setCards(getMetaForFormat(fmt.key).cards);
                }
            } catch {
                if (!cancelled) {
                    const seed = getMetaForFormat(fmt.key);
                    setArchetypes(seed.archetypes);
                    setCards(seed.cards);
                }
            } finally {
                if (!cancelled) setDbLoading(false);
            }
        }
        loadMeta();
        // Also fetch last updated time
        metaService.getLastUpdated().then(ts => {
            if (ts && !cancelled) setLastUpdated(ts);
        });
        return () => { cancelled = true; };
    }, [fmt.key]);

    // Helper: format relative time
    const formatRelativeTime = (iso: string) => {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    // Manual refresh handler
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await metaService.refreshPipeline(fmt.key);
            // Reload after pipeline finishes
            const [dbArch, dbCards] = await Promise.all([
                metaService.getArchetypes(fmt.key),
                metaService.getCardStats(fmt.key),
            ]);
            if (dbArch.length > 0) {
                setArchetypes(dbArch.map(a => ({
                    name: a.name, colors: a.colors as ManaColor[], tier: a.tier as Tier,
                    fieldShare: Number(a.field_share), winRate: Number(a.win_rate),
                    trend: a.trend as Trend, keyCards: a.key_cards || [], matches: a.matches,
                })));
                setDataSource('db');
            }
            if (dbCards.length > 0) {
                setCards(dbCards.map(c => ({
                    name: c.card_name, colors: c.colors as ManaColor[],
                    inHandWR: Number(c.in_hand_wr), playedWR: Number(c.played_wr),
                    includedWR: Number(c.included_wr), totalGames: c.total_games,
                })));
            }
            const ts = await metaService.getLastUpdated();
            if (ts) setLastUpdated(ts);
        } catch (err) {
            console.error('refresh failed:', err);
        } finally {
            setRefreshing(false);
        }
    };

    // ─── Realtime: auto-refresh when pipeline publishes new snapshots ────────
    const [realtimeToast, setRealtimeToast] = useState(false);

    useEffect(() => {
        const channel = supabase
            .channel('meta-snapshots-realtime')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'meta_archetype_snapshots',
            }, async (payload: any) => {
                // Only refresh if the inserted snapshot matches our current format
                const snap = payload.new;
                if (!snap || snap.format !== fmt.key) return;

                console.log('[realtime] New meta snapshot detected, refreshing...');

                // Reload archetypes from DB
                try {
                    const dbArch = await metaService.getArchetypes(fmt.key);
                    if (dbArch.length > 0) {
                        setArchetypes(dbArch.map(a => ({
                            name: a.name, colors: a.colors as ManaColor[], tier: a.tier as Tier,
                            fieldShare: Number(a.field_share), winRate: Number(a.win_rate),
                            trend: a.trend as Trend, keyCards: a.key_cards || [], matches: a.matches,
                        })));
                        setDataSource('db');
                    }
                    const ts = await metaService.getLastUpdated();
                    if (ts) setLastUpdated(ts);

                    // Show brief toast
                    setRealtimeToast(true);
                    setTimeout(() => setRealtimeToast(false), 3000);
                } catch (err) {
                    console.error('[realtime] refresh error:', err);
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [fmt.key]);

    const totalMatches = archetypes.reduce((a, b) => a + b.matches, 0);

    // Debounced search
    const doSearch = useCallback((q: string) => {
        setSq(q); if (stRef.current) clearTimeout(stRef.current);
        if (q.length < 2) { setSr([]); return; }
        setSrch(true); stRef.current = setTimeout(async () => { setSr(await searchScryfall(q)); setSrch(false); }, 300);
    }, []);

    const addCard = (c: any) => {
        if (wl.find(w => w.name === c.name)) return;
        setWl(p => [...p, { id: crypto.randomUUID(), name: c.name, copies: fmt.key === 'commander' ? 1 : 4, manaCost: c.mana_cost || '', typeLine: c.type_line || '', imageUrl: getImg(c), category: classifyCard(c) }]);
        setSq(''); setSr([]); setShowSrch(false);
    };

    const filteredWL = catF === 'all' ? wl : wl.filter(c => c.category === catF);
    const isEDH = fmt.key === 'commander'; const onPlay = !isEDH;

    const catSummary = Object.entries(CAT_META).map(([k, m]) => ({ key: k as ThreatCat, ...m, count: wl.filter(c => c.category === k).length })).filter(c => c.count > 0);

    // Tier list data
    const filteredArch = tierFilter === 'all' ? archetypes : archetypes.filter(a => a.tier === tierFilter);
    const tiers: Tier[] = ['S', 'A', 'B', 'C'];

    // Card data sorted
    const sortedCards = [...cards].sort((a, b) => b[cardSort] - a[cardSort]);

    // ─── Overview columns (like Untapped) ────────────────────────
    const byWR = [...archetypes].sort((a, b) => b.winRate - a.winRate).slice(0, 8);
    const byPop = [...archetypes].sort((a, b) => b.fieldShare - a.fieldShare).slice(0, 8);
    const rising = archetypes.filter(a => a.trend === 'rising').slice(0, 5);

    // Combined threat probs
    const combinedP: number[] = [];
    for (let t = 0; t <= fmt.maxT; t++) {
        const s = cardsSeen(t, onPlay); let pN = 1;
        for (const c of filteredWL) { pN *= 1 - pAtLeast1(fmt.deck, c.copies, Math.min(s, fmt.deck)); }
        combinedP.push(+((1 - pN) * 100).toFixed(1));
    }

    const copiesNeeded = (tgt: number, turn: number) => { const s = cardsSeen(turn, onPlay); for (let c = 1; c <= fmt.deck; c++) if (pAtLeast1(fmt.deck, c, Math.min(s, fmt.deck)) * 100 >= tgt) return c; return fmt.deck; };

    // ═════════════════════════════════════════════════════════════
    return (
        <div className="flex flex-col gap-3 h-full overflow-hidden">
            {/* Format + View Tabs */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Format pills */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    {FMTS.map(f => (
                        <button key={f.key} onClick={() => setFmt(f)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border ${fmt.key === f.key ? `${f.bg} ${f.color} ${f.border} shadow-lg` : 'bg-white/5 text-white/40 border-white/8 hover:bg-white/10'}`}>
                            <span>{f.icon}</span>{f.label}
                        </button>
                    ))}
                </div>
                <div className="w-px h-5 bg-white/10" />
                {/* View tabs */}
                {([
                    { k: 'overview' as MetaView, label: 'Meta', icon: <Activity className="size-3.5" /> },
                    { k: 'tierlist' as MetaView, label: 'Tier List', icon: <Crown className="size-3.5" /> },
                    { k: 'cards' as MetaView, label: 'Card Data', icon: <CreditCard className="size-3.5" /> },
                    { k: 'threats' as MetaView, label: 'Threats', icon: <Crosshair className="size-3.5" /> },
                    { k: 'report' as MetaView, label: 'Report', icon: <Plus className="size-3.5" /> },
                ]).map(v => (
                    <button key={v.k} onClick={() => setView(v.k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${view === v.k ? 'bg-white/10 text-white border-white/20' : 'bg-transparent text-white/40 border-transparent hover:text-white/60'}`}>
                        {v.icon}{v.label}
                    </button>
                ))}
                {/* Total matches badge */}
                {totalMatches > 0 && <span className="ml-auto text-[10px] text-white/30">Total matches: <span className="text-white/50 font-bold">{totalMatches.toLocaleString()}</span></span>}
                {/* Last Updated + Refresh */}
                <div className="flex items-center gap-2 ml-2">
                    {lastUpdated && (
                        <span className="flex items-center gap-1 text-[10px] text-white/25" title={`Last updated: ${new Date(lastUpdated).toLocaleString()}`}>
                            <Clock className="size-3" />
                            {formatRelativeTime(lastUpdated)}
                        </span>
                    )}
                    {dataSource === 'seed' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400/60 border border-amber-500/15">sample data</span>
                    )}
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-white/60 transition-colors disabled:opacity-30"
                        title="Refresh meta data from sources"
                    >
                        <RefreshCw className={`size-3 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Realtime update toast */}
            {realtimeToast && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium animate-pulse">
                    <Activity className="size-3" />
                    Meta data updated!
                </div>
            )}

            {/* ═══ OVERVIEW ═══ */}
            {view === 'overview' && (
                <div className="flex-1 overflow-y-auto">
                    {archetypes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center gap-3">
                            <Layers className="size-10 text-white/15" />
                            <p className="text-sm text-white/40">Meta data for <span className="text-white/60 font-medium">{fmt.label}</span> coming soon</p>
                            <p className="text-xs text-white/25">Try Standard or Pioneer for current data</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-3">
                                {/* Win Rate Today */}
                                <div className="bg-white/3 border border-white/10 rounded-xl p-3">
                                    <h4 className="text-xs font-semibold text-white/70 mb-2 flex items-center gap-1.5"><Trophy className="size-3.5 text-amber-400" />Win Rate Today</h4>
                                    <div className="flex flex-col gap-0.5">
                                        {byWR.map((a, i) => (
                                            <div key={a.name + i} className="group flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                                                <span className="text-[10px] text-white/30 w-4 font-bold">#{i + 1}</span>
                                                <ColorPips colors={a.colors} />
                                                <span className="text-xs text-white/80 truncate max-w-[120px]">{a.name}</span>
                                                <span className="text-[10px] text-white/30">{a.fieldShare.toFixed(1)}%</span>
                                                <span className="flex-1" />
                                                <WRBadge wr={a.winRate} />
                                                <div className="hidden group-hover:flex items-center gap-1 ml-1">
                                                    <button onClick={() => setExpandedArch(a)} className="p-0.5 rounded bg-white/5 hover:bg-white/15 text-white/40 hover:text-white transition-colors" title="View key cards"><Eye className="size-3" /></button>
                                                    <button onClick={() => examineInBrew(a)} className="p-0.5 rounded bg-cyan-500/10 hover:bg-cyan-500/25 text-cyan-400/60 hover:text-cyan-400 transition-colors" title="Examine in Brew"><FlaskConical className="size-3" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Popularity Today */}
                                <div className="bg-white/3 border border-white/10 rounded-xl p-3">
                                    <h4 className="text-xs font-semibold text-white/70 mb-2 flex items-center gap-1.5"><BarChart3 className="size-3.5 text-purple-400" />Popularity Today</h4>
                                    <div className="flex flex-col gap-0.5">
                                        {byPop.map((a, i) => (
                                            <div key={a.name + i} className="group flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                                                <span className="text-[10px] text-white/30 w-4 font-bold">#{i + 1}</span>
                                                <ColorPips colors={a.colors} />
                                                <span className="text-xs text-white/80 truncate max-w-[120px]">{a.name}</span>
                                                <span className="text-xs font-bold text-purple-400 tabular-nums">{a.fieldShare.toFixed(1)}%</span>
                                                <span className="flex-1" />
                                                <div className="hidden group-hover:flex items-center gap-1">
                                                    <button onClick={() => setExpandedArch(a)} className="p-0.5 rounded bg-white/5 hover:bg-white/15 text-white/40 hover:text-white transition-colors" title="View key cards"><Eye className="size-3" /></button>
                                                    <button onClick={() => examineInBrew(a)} className="p-0.5 rounded bg-cyan-500/10 hover:bg-cyan-500/25 text-cyan-400/60 hover:text-cyan-400 transition-colors" title="Examine in Brew"><FlaskConical className="size-3" /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Trending */}
                                <div className="bg-white/3 border border-white/10 rounded-xl p-3">
                                    <h4 className="text-xs font-semibold text-white/70 mb-2 flex items-center gap-1.5"><TrendingUp className="size-3.5 text-green-400" />Rising Archetypes</h4>
                                    {rising.length === 0 ? (
                                        <p className="text-xs text-white/30 py-4 text-center">No rising archetypes detected</p>
                                    ) : (
                                        <div className="flex flex-col gap-0.5">
                                            {rising.map((a, i) => (
                                                <div key={a.name + i} className="group flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                                                    <ArrowUpRight className="size-3 text-green-400" />
                                                    <ColorPips colors={a.colors} />
                                                    <span className="text-xs text-white/80 truncate max-w-[120px]">{a.name}</span>
                                                    <span className="text-[10px] text-white/30">{a.fieldShare.toFixed(1)}%</span>
                                                    <span className="flex-1" />
                                                    <WRBadge wr={a.winRate} />
                                                    <div className="hidden group-hover:flex items-center gap-1 ml-1">
                                                        <button onClick={() => setExpandedArch(a)} className="p-0.5 rounded bg-white/5 hover:bg-white/15 text-white/40 hover:text-white transition-colors" title="View key cards"><Eye className="size-3" /></button>
                                                        <button onClick={() => examineInBrew(a)} className="p-0.5 rounded bg-cyan-500/10 hover:bg-cyan-500/25 text-cyan-400/60 hover:text-cyan-400 transition-colors" title="Examine in Brew"><FlaskConical className="size-3" /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Quick meta field share chart */}
                                    <div className="mt-4 pt-3 border-t border-white/8">
                                        <h5 className="text-[10px] text-white/40 mb-2">Field Distribution</h5>
                                        {byPop.slice(0, 5).map(a => (
                                            <div key={a.name} className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] text-white/50 w-24 truncate">{a.name}</span>
                                                <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${Math.min(a.fieldShare * 5, 100)}%` }} />
                                                </div>
                                                <span className="text-[10px] text-white/40 w-8 text-right">{a.fieldShare}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Archetype Detail Overlay */}
                            {expandedArch && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setExpandedArch(null)}>
                                    <div className="bg-[#0a0f1a] border border-white/15 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
                                        {/* Header */}
                                        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/3">
                                            <ColorPips colors={expandedArch.colors} />
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-bold text-white truncate">{expandedArch.name}</h3>
                                                <div className="flex items-center gap-3 mt-0.5">
                                                    <span className="text-[10px] text-white/40">{expandedArch.fieldShare.toFixed(1)}% field share</span>
                                                    <WRBadge wr={expandedArch.winRate} />
                                                    <TrendIcon trend={expandedArch.trend} />
                                                </div>
                                            </div>
                                            <button onClick={() => setExpandedArch(null)} className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors">
                                                <X className="size-4" />
                                            </button>
                                        </div>

                                        {/* Key Cards */}
                                        <div className="px-5 py-4">
                                            <h4 className="text-[11px] font-semibold text-white/50 uppercase tracking-wider mb-3">Key Cards</h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {expandedArch.keyCards.map(card => (
                                                    <div key={card} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/8 hover:bg-white/8 transition-colors">
                                                        <span className="text-xs text-white/80">{card}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Tier + Stats */}
                                        <div className="px-5 py-3 border-t border-white/8 bg-white/2 flex items-center gap-4">
                                            <span className={`text-xs font-black px-2 py-0.5 rounded ${TIER_META[expandedArch.tier].bg} ${TIER_META[expandedArch.tier].color} border ${TIER_META[expandedArch.tier].border}`}>Tier {expandedArch.tier}</span>
                                            <span className="text-[11px] text-white/40 flex items-center gap-1.5">
                                                {expandedArch.matches.toLocaleString()} matches
                                                {expandedArch.matches >= 1000 ? (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-green-500/10 text-green-400/70 border border-green-500/15">High confidence</span>
                                                ) : expandedArch.matches >= 100 ? (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400/70 border border-amber-500/15">Moderate</span>
                                                ) : (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-gray-500/10 text-gray-400/70 border border-gray-500/15">Low sample</span>
                                                )}
                                            </span>
                                            <span className="flex-1" />
                                            <button onClick={() => { examineInBrew(expandedArch); setExpandedArch(null); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 text-xs font-medium border border-cyan-500/25 hover:bg-cyan-500/25 transition-colors">
                                                <FlaskConical className="size-3" /> Examine in Brew
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ═══ TIER LIST ═══ */}
            {view === 'tierlist' && (
                <div className="flex-1 overflow-y-auto">
                    {/* Tier filter */}
                    <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => setTierFilter('all')} className={`px-3 py-1 rounded-lg text-xs font-medium transition-all border ${tierFilter === 'all' ? 'bg-white/10 text-white border-white/20' : 'bg-white/3 text-white/40 border-white/8 hover:text-white/60'}`}>All Tiers</button>
                        {tiers.map(t => (
                            <button key={t} onClick={() => setTierFilter(t)} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all border ${tierFilter === t ? `${TIER_META[t].bg} ${TIER_META[t].color} ${TIER_META[t].border}` : 'bg-white/3 text-white/40 border-white/8 hover:text-white/60'}`}>
                                Tier {t}
                            </button>
                        ))}
                    </div>

                    {archetypes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-2"><Layers className="size-8 text-white/15" /><p className="text-sm text-white/40">No tier data for {fmt.label} yet</p></div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {tiers.filter(t => tierFilter === 'all' || tierFilter === t).map(tier => {
                                const archs = filteredArch.filter(a => a.tier === tier);
                                if (archs.length === 0) return null;
                                return (
                                    <div key={tier}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`text-sm font-black ${TIER_META[tier].color} ${TIER_META[tier].bg} px-2 py-0.5 rounded ${TIER_META[tier].border} border`}>Tier {tier}</span>
                                            <span className="text-[10px] text-white/30">{archs.length} archetype{archs.length !== 1 ? 's' : ''}</span>
                                        </div>
                                        <div className="grid grid-cols-1 gap-1.5">
                                            {archs.map(a => (
                                                <div key={a.name} className="flex items-center gap-3 px-3 py-2.5 bg-white/3 border border-white/8 rounded-lg hover:bg-white/6 transition-colors group">
                                                    <ColorPips colors={a.colors} />
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <span className="text-sm text-white font-medium truncate">{a.name}</span>
                                                        <span className="text-xs font-bold text-purple-400 tabular-nums shrink-0">{a.fieldShare.toFixed(1)}%</span>
                                                    </div>
                                                    <TrendIcon trend={a.trend} />
                                                    <div className="flex items-center gap-4 text-xs">
                                                        <div className="text-center w-16">
                                                            <div className="text-[9px] text-white/30">Win Rate</div>
                                                            <WRBadge wr={a.winRate} />
                                                        </div>
                                                        <div className="text-center w-16">
                                                            <div className="text-[9px] text-white/30">Matches</div>
                                                            <div className="text-white/50 tabular-nums">{(a.matches / 1000).toFixed(1)}k</div>
                                                        </div>
                                                    </div>
                                                    {/* Field share bar */}
                                                    <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full bg-gradient-to-r ${a.winRate >= 55 ? 'from-green-500 to-emerald-500' : a.winRate >= 52 ? 'from-amber-500 to-yellow-500' : 'from-gray-500 to-gray-600'}`} style={{ width: `${Math.min(a.fieldShare * 5, 100)}%` }} />
                                                    </div>
                                                    {/* Action buttons */}
                                                    <div className="hidden group-hover:flex items-center gap-1">
                                                        <button onClick={() => setExpandedArch(a)} className="p-1 rounded bg-white/5 hover:bg-white/15 text-white/40 hover:text-white transition-colors" title="View key cards"><Eye className="size-3.5" /></button>
                                                        <button onClick={() => examineInBrew(a)} className="p-1 rounded bg-cyan-500/10 hover:bg-cyan-500/25 text-cyan-400/60 hover:text-cyan-400 transition-colors" title="Examine in Brew"><FlaskConical className="size-3.5" /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ CARD DATA ═══ */}
            {view === 'cards' && (
                <div className="flex-1 overflow-y-auto">
                    <div className="text-xs text-white/40 mb-3 flex items-center gap-2">
                        <Info className="size-3" /> Detailed card statistics — win rate when in hand, when played, and when included in deck
                    </div>
                    {cards.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 gap-2"><CreditCard className="size-8 text-white/15" /><p className="text-sm text-white/40">No card data for {fmt.label} yet</p></div>
                    ) : (
                        <div className="bg-white/3 border border-white/10 rounded-xl overflow-hidden">
                            {/* Table header */}
                            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-2 border-b border-white/10 bg-white/3">
                                {[
                                    { k: 'name' as const, label: 'Card' },
                                    { k: 'inHandWR' as const, label: 'In Hand WR ↕' },
                                    { k: 'playedWR' as const, label: 'Played WR' },
                                    { k: 'includedWR' as const, label: 'Included WR' },
                                    { k: 'totalGames' as const, label: 'Total Games' },
                                ].map(col => (
                                    <button
                                        key={col.k}
                                        onClick={() => { if (col.k !== 'name') setCardSort(col.k as any); }}
                                        className={`text-[10px] font-semibold text-left transition-colors ${col.k !== 'name' && cardSort === col.k ? 'text-amber-400' : 'text-white/50 hover:text-white/70'}`}
                                    >
                                        {col.label}
                                    </button>
                                ))}
                            </div>
                            {/* Table rows */}
                            {sortedCards.map((c, i) => (
                                <div key={c.name + i} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-2.5 border-b border-white/5 hover:bg-white/5 transition-colors items-center">
                                    <div className="flex items-center gap-2">
                                        <ColorPips colors={c.colors} />
                                        <span className="text-sm text-white font-medium truncate">{c.name}</span>
                                    </div>
                                    <div><WRBadge wr={c.inHandWR} /></div>
                                    <div><WRBadge wr={c.playedWR} /></div>
                                    <div><WRBadge wr={c.includedWR} /></div>
                                    <div className="text-xs text-white/50 tabular-nums">{c.totalGames.toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ THREAT ANALYSIS ═══ (our unique differentiator) */}
            {view === 'threats' && (
                <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
                    {/* LEFT: Watchlist */}
                    <div className="w-[300px] flex flex-col gap-2 overflow-y-auto pr-1 shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2"><Crosshair className="size-3.5 text-red-400" /><h3 className="text-xs font-semibold text-white">Threat Watchlist</h3><span className="text-[10px] text-white/30">{wl.length}</span></div>
                            <button onClick={() => { setShowSrch(!showSrch); setTimeout(() => siRef.current?.focus(), 100); }} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-all border ${showSrch ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25'}`}>
                                {showSrch ? <X className="size-3" /> : <Plus className="size-3" />}{showSrch ? 'Close' : 'Add'}
                            </button>
                        </div>

                        {showSrch && (
                            <div className="relative">
                                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
                                    <Search className="size-3 text-white/40" />
                                    <input ref={siRef} type="text" value={sq} onChange={e => doSearch(e.target.value)} placeholder="Search cards..." className="bg-transparent text-xs text-white placeholder-white/30 outline-none flex-1" />
                                    {srch && <div className="size-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                </div>
                                {sr.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900/95 border border-white/15 rounded-lg shadow-2xl z-50 max-h-48 overflow-y-auto backdrop-blur-md">
                                        {sr.map((c: any) => (
                                            <button key={c.id} onClick={() => addCard(c)} className="flex items-center gap-2 w-full px-3 py-1.5 hover:bg-white/10 transition-colors text-left border-b border-white/5 last:border-0">
                                                {getImg(c) && <img src={getImg(c)} alt="" className="w-6 h-9 rounded object-cover" />}
                                                <div className="flex-1 min-w-0"><div className="text-xs text-white truncate">{c.name}</div><div className="text-[9px] text-white/30 truncate">{c.type_line}</div></div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {wl.length > 0 && catSummary.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                                <button onClick={() => setCatF('all')} className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${catF === 'all' ? 'bg-white/15 text-white' : 'bg-white/5 text-white/40'}`}>All</button>
                                {catSummary.map(c => <button key={c.key} onClick={() => setCatF(c.key)} className={`px-1.5 py-0.5 rounded text-[9px] ${catF === c.key ? `${c.bg} ${c.color}` : 'bg-white/5 text-white/40'}`}>{c.icon}{c.count}</button>)}
                            </div>
                        )}

                        {filteredWL.length === 0 ? (
                            <div className="flex flex-col items-center py-8 gap-2 text-center">
                                <Target className="size-8 text-white/15" />
                                <p className="text-xs text-white/40">Add cards to track</p>
                                <button onClick={() => { setShowSrch(true); setTimeout(() => siRef.current?.focus(), 100); }} className="text-xs text-red-400 hover:text-red-300">+ Add threat</button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                {filteredWL.map(card => {
                                    const turns: number[] = []; for (let t = 0; t <= fmt.maxT; t++) turns.push(+(pAtLeast1(fmt.deck, card.copies, Math.min(cardsSeen(t, onPlay), fmt.deck)) * 100).toFixed(1));
                                    const peak = turns[turns.length - 1]; const isAct = activeId === card.id; const cm = CAT_META[card.category];
                                    return (
                                        <div key={card.id} onClick={() => setActiveId(isAct ? null : card.id)} className={`group rounded-lg border transition-all cursor-pointer ${isAct ? 'bg-white/8 border-white/20' : 'bg-white/3 border-white/8 hover:bg-white/6'}`}>
                                            <div className="flex items-center gap-2 px-2.5 py-1.5">
                                                {card.imageUrl && <img src={card.imageUrl} alt="" className="w-6 h-9 rounded object-cover shrink-0" />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs text-white font-medium truncate">{card.name}</div>
                                                    <div className="flex items-center gap-1.5 mt-0.5"><span className={`text-[9px] ${cm.color} ${cm.bg} px-1 rounded`}>{cm.icon}{cm.label}</span><span className="text-[9px] text-white/25">{card.copies}x</span></div>
                                                </div>
                                                <div className="text-right shrink-0"><div className={`text-sm font-bold tabular-nums ${riskTxt(peak)}`}>{peak}%</div><div className="text-[8px] text-white/25">T{fmt.maxT}</div></div>
                                                <button onClick={e => { e.stopPropagation(); setWl(p => p.filter(c => c.id !== card.id)); if (activeId === card.id) setActiveId(null); }} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-500/20 rounded"><Trash2 className="size-3 text-red-400" /></button>
                                            </div>
                                            {isAct && (
                                                <div className="px-2.5 pb-2 pt-1 border-t border-white/8">
                                                    <div className="flex items-center gap-1 mb-1.5">
                                                        <span className="text-[9px] text-white/30">Copies:</span>
                                                        {[1, 2, 3, 4].map(n => <button key={n} onClick={e => { e.stopPropagation(); setWl(p => p.map(c => c.id === card.id ? { ...c, copies: n } : c)); }} className={`size-4 rounded text-[9px] font-bold ${card.copies === n ? 'bg-white/20 text-white' : 'bg-white/5 text-white/40'}`}>{n}</button>)}
                                                    </div>
                                                    {turns.map((p, t) => (
                                                        <div key={t} className="flex items-center gap-1.5 mb-0.5">
                                                            <span className="text-[9px] text-white/30 w-5 text-right font-mono">{t === 0 ? 'Op' : `T${t}`}</span>
                                                            <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${riskCol(p)}`} style={{ width: `${Math.max(p, 1)}%` }} /></div>
                                                            <span className={`text-[9px] font-mono w-8 text-right tabular-nums ${riskTxt(p)}`}>{p.toFixed(1)}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: Combined + Calculator */}
                    <div className="flex-1 flex flex-col gap-3 overflow-y-auto">
                        {filteredWL.length > 0 && (
                            <div className="bg-white/3 border border-white/10 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3"><BarChart3 className="size-4 text-red-400" /><h3 className="text-sm font-semibold text-white">Combined Threat Radar</h3><span className="text-[10px] text-white/30">P(any) across {filteredWL.length} card{filteredWL.length !== 1 ? 's' : ''}</span></div>
                                {combinedP.map((p, t) => (
                                    <div key={t} className="flex items-center gap-3 mb-1">
                                        <span className="text-xs text-white/40 w-10 text-right font-mono">{t === 0 ? 'Open' : `Turn ${t}`}</span>
                                        <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${riskCol(p)} transition-all`} style={{ width: `${Math.max(p, 1)}%` }}>{p > 15 && <span className="absolute right-2 top-0.5 text-[10px] font-bold text-white/90">{p}%</span>}</div></div>
                                        <span className={`text-xs font-bold w-12 text-right tabular-nums ${riskTxt(p)}`}>{p}%</span>
                                        <span className={`text-[10px] w-14 ${riskTxt(p)}`}>{riskLbl(p)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Interaction calculator */}
                        <div className="bg-white/3 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3"><Shield className="size-4 text-blue-400" /><h3 className="text-sm font-semibold text-white">Interaction Density Calculator</h3></div>
                            <p className="text-xs text-white/40 mb-3">How many copies to see an answer by a given turn?</p>
                            <div className="flex items-center gap-4 mb-3">
                                <div className="flex items-center gap-2"><span className="text-xs text-white/50">Target:</span><input type="range" min="10" max="95" step="5" value={intTarget} onChange={e => setIntTarget(+e.target.value)} className="w-20 h-1 accent-blue-500" /><span className="text-xs text-blue-400 font-bold">{intTarget}%</span></div>
                                <div className="flex items-center gap-1"><span className="text-xs text-white/50">By:</span>{Array.from({ length: fmt.maxT + 1 }, (_, i) => i).map(t => <button key={t} onClick={() => setIntTurn(t)} className={`size-5 rounded text-[9px] font-bold ${intTurn === t ? 'bg-blue-500/30 text-blue-400 border border-blue-500/40' : 'bg-white/5 text-white/40'}`}>{t === 0 ? 'Op' : `T${t}`}</button>)}</div>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {[30, 50, 70, 90].map(tgt => {
                                    const n = copiesNeeded(tgt, intTurn); return (
                                        <div key={tgt} className={`rounded-lg p-2.5 text-center border ${tgt === intTarget ? 'bg-blue-500/15 border-blue-500/30' : 'bg-white/3 border-white/8'}`}>
                                            <div className="text-[9px] text-white/40">≥{tgt}% by T{intTurn}</div>
                                            <div className={`text-xl font-black ${tgt === intTarget ? 'text-blue-400' : 'text-white/60'}`}>{n}</div>
                                            <div className="text-[9px] text-white/25">{n === 1 ? 'copy' : 'copies'}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-3 p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-2">
                                <Shield className="size-4 text-blue-400 shrink-0" />
                                <div><div className="text-xs text-white">Need <span className="text-blue-400 font-black text-base">{copiesNeeded(intTarget, intTurn)}</span> copies for ≥{intTarget}% by {intTurn === 0 ? 'opener' : `T${intTurn}`}</div><div className="text-[9px] text-white/30">{fmt.label} · {fmt.deck} cards · {cardsSeen(intTurn, onPlay)} seen</div></div>
                            </div>
                        </div>

                        {/* Heuristics */}
                        <div className="bg-white/3 border border-white/10 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2"><Info className="size-3 text-white/30" /><span className="text-[10px] font-semibold text-white/50">Competitive Heuristics</span></div>
                            <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                                <div className="bg-white/3 rounded-lg p-1.5 border border-white/5"><span className="text-white/70 font-bold">4x</span> <span className="text-white/40">Must see early</span></div>
                                <div className="bg-white/3 rounded-lg p-1.5 border border-white/5"><span className="text-white/70 font-bold">3x</span> <span className="text-white/40">Strong, not mandatory</span></div>
                                <div className="bg-white/3 rounded-lg p-1.5 border border-white/5"><span className="text-white/70 font-bold">2x</span> <span className="text-white/40">Matchup hedge</span></div>
                                <div className="bg-white/3 rounded-lg p-1.5 border border-white/5"><span className="text-white/70 font-bold">1x</span> <span className="text-white/40">Silver bullet</span></div>
                            </div>
                        </div>

                        {filteredWL.length === 0 && (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                                <Swords className="size-10 text-red-400/30" />
                                <p className="text-sm text-white/40">Add threats to see combined probability heatmaps</p>
                                <button onClick={() => { setShowSrch(true); setTimeout(() => siRef.current?.focus(), 100); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 text-red-400 border border-red-500/25 rounded-lg text-xs hover:bg-red-500/25"><Plus className="size-3" />Add Threat</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* ═══ REPORT MATCH ═══ */}
            {view === 'report' && (
                <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
                        <h3 className="text-sm font-semibold text-emerald-400 mb-1 flex items-center gap-2"><BarChart3 className="size-4" /> Report a Match</h3>
                        <p className="text-[11px] text-white/40 mb-4">Help the community by reporting your match results. Reports are aggregated into field share and win rate data.</p>

                        {reportSent ? (
                            <div className="flex flex-col items-center gap-3 py-6">
                                <div className="size-12 rounded-full bg-emerald-500/20 flex items-center justify-center"><Trophy className="size-6 text-emerald-400" /></div>
                                <p className="text-sm font-medium text-emerald-400">Report submitted!</p>
                                <p className="text-[11px] text-white/40">Thanks for contributing to the community meta.</p>
                                <button onClick={() => { setReportSent(false); setReportArch(''); setReportOpp(''); }} className="px-4 py-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 rounded-lg text-xs hover:bg-emerald-500/25">Report Another</button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* My Archetype */}
                                <div>
                                    <label className="text-[11px] text-white/50 font-medium block mb-1">My Archetype</label>
                                    <select value={reportArch} onChange={e => setReportArch(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-emerald-500/40">
                                        <option value="" className="bg-gray-900">Select archetype...</option>
                                        {archetypes.map(a => <option key={a.name} value={a.name} className="bg-gray-900">{a.name}</option>)}
                                        <option value="_custom" className="bg-gray-900">Other / Homebrew</option>
                                    </select>
                                </div>
                                {/* Opponent Archetype */}
                                <div>
                                    <label className="text-[11px] text-white/50 font-medium block mb-1">Opponent Archetype</label>
                                    <select value={reportOpp} onChange={e => setReportOpp(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-emerald-500/40">
                                        <option value="" className="bg-gray-900">Select archetype...</option>
                                        {archetypes.map(a => <option key={a.name} value={a.name} className="bg-gray-900">{a.name}</option>)}
                                        <option value="Unknown" className="bg-gray-900">Unknown</option>
                                    </select>
                                </div>
                                {/* Result + BoX */}
                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <label className="text-[11px] text-white/50 font-medium block mb-1">Result</label>
                                        <div className="flex gap-1">
                                            {(['win', 'loss', 'draw'] as const).map(r => (
                                                <button key={r} onClick={() => setReportResult(r)} className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${reportResult === r ? (r === 'win' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : r === 'loss' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30') : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'}`}>
                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="w-24">
                                        <label className="text-[11px] text-white/50 font-medium block mb-1">Format</label>
                                        <div className="flex gap-1">
                                            {(['bo1', 'bo3'] as const).map(b => (
                                                <button key={b} onClick={() => setReportBo(b)} className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${reportBo === b ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-white/5 text-white/40 border-white/10 hover:bg-white/10'}`}>
                                                    {b.toUpperCase()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {/* Submit */}
                                <button
                                    disabled={!reportArch || !reportOpp || reportSending}
                                    onClick={async () => {
                                        setReportSending(true);
                                        const res = await metaService.submitMatchReport({ format: fmt.key, myArchetype: reportArch, opponentArchetype: reportOpp, result: reportResult, matchFormat: reportBo });
                                        setReportSending(false);
                                        if (res.ok) {
                                            setReportSent(true);
                                        } else {
                                            alert(res.error || 'Failed to submit report');
                                        }
                                    }}
                                    className="w-full py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-emerald-500/20 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/30"
                                >
                                    {reportSending ? 'Submitting...' : 'Submit Match Report'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Info callout */}
                    <div className="rounded-xl border border-white/8 bg-white/3 p-4">
                        <h4 className="text-[11px] font-semibold text-white/60 mb-2 flex items-center gap-1.5"><Info className="size-3" /> How Community Meta Works</h4>
                        <ul className="space-y-1 text-[10px] text-white/40 list-disc list-inside">
                            <li>Report your matches to help build accurate field share and win rate data.</li>
                            <li>Reports are <span className="text-white/60">trust-weighted</span> — verified members, mods, and LGS owners carry more influence.</li>
                            <li>Matchup summaries require at least 5 reports before displaying.</li>
                            <li>Suspicious patterns (e.g. spamming the same matchup) are auto-flagged.</li>
                            <li>Rate limits apply: 20 reports/day for standard users, more for verified roles.</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}
